import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, LockKeyhole, Mail, Phone, ShieldCheck } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '@/core/hooks';
import { getDashboardRoute } from '@/core/utils';
import type { User } from '@/core/types/auth';

const SUPERADMIN_EMAIL = 'BadanCatalin@admin.com';
const SUPERADMIN_PASSWORD = 'admin@catalin.123';
const MARIUS_SUPERADMIN_EMAIL = 'MariusDorobat@superadmin.com';
const MARIUS_SUPERADMIN_PASSWORD = 'marius@superadmin.12377';
const CARER_EMAIL = 'AdrianPopescu@carer.com';
const CARER_PASSWORD = 'adrian@carer.123';
const FAMILY_EMAIL = 'jhony@family.com';
const FAMILY_PASSWORD = 'jhony@family.123';

const normalizeEmail = (value: string) => value.trim().toLowerCase();
const normalizePassword = (value: string) => value.trim();

const SUPERADMIN_USER: User = {
  id: 'superadmin-fuchsia-catalin',
  organizationId: 'org-1',
  email: SUPERADMIN_EMAIL,
  name: 'Badan Catalin',
  roles: ['ADMIN'],
  permissions: [
    'admin:access',
    'clients:read',
    'clients:write',
    'caregivers:read',
    'visits:read',
    'visits:write',
    'care_plans:read',
    'care_plans:create',
    'tasks:read',
    'tasks:write',
    'evv:read',
    'billing:read',
    'incidents:read',
    'audits:view',
    'compliance:read',
  ],
};

const MARIUS_SUPERADMIN_USER: User = {
  id: 'superadmin-marius-dorobat',
  organizationId: 'org-1',
  email: MARIUS_SUPERADMIN_EMAIL,
  name: 'Marius Dorobat',
  roles: ['SUPER_ADMIN'],
  permissions: ['*'],
};

const CARER_USER: User = {
  id: 'caregiver-1',
  organizationId: 'org-1',
  email: CARER_EMAIL,
  name: 'Adrian Popescu',
  roles: ['CAREGIVER'],
  permissions: [
    'tasks:read',
    'tasks:write',
    'visits:read',
    'evv:read',
    'evv:write',
  ],
};

const FAMILY_USER: User = {
  id: 'family-member-1',
  organizationId: 'org-1',
  email: FAMILY_EMAIL,
  name: 'Jhony Williams',
  roles: ['FAMILY'],
  permissions: [
    'family:read',
    'family:message',
    'family:notifications',
  ],
};

const LOGIN_ACCOUNTS = [
  {
    email: SUPERADMIN_EMAIL,
    password: SUPERADMIN_PASSWORD,
    user: SUPERADMIN_USER,
    token: 'local-superadmin-token',
    welcome: 'Welcome, Badan Catalin!',
    errorContext: 'superadmin, carer, or family credentials',
  },
  {
    email: MARIUS_SUPERADMIN_EMAIL,
    password: MARIUS_SUPERADMIN_PASSWORD,
    user: MARIUS_SUPERADMIN_USER,
    token: 'local-superadmin-marius-token',
    welcome: 'Welcome, Marius Dorobat!',
    errorContext: 'superadmin, carer, or family credentials',
  },
  {
    email: CARER_EMAIL,
    password: CARER_PASSWORD,
    user: CARER_USER,
    token: 'local-carer-token',
    welcome: 'Welcome, Adrian Popescu!',
    errorContext: 'superadmin, carer, or family credentials',
  },
  {
    email: FAMILY_EMAIL,
    password: FAMILY_PASSWORD,
    user: FAMILY_USER,
    token: 'local-family-token',
    welcome: 'Welcome, Jhony!',
    errorContext: 'superadmin, carer, or family credentials',
  },
];

export const Login: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; password?: string; form?: string }>({});

  useEffect(() => {
    const clearLoginFields = () => {
      setEmail('');
      setPassword('');

      const emailInput = document.querySelector<HTMLInputElement>('#email');
      const passwordInput = document.querySelector<HTMLInputElement>('#password');

      if (emailInput) emailInput.value = '';
      if (passwordInput) passwordInput.value = '';
    };

    clearLoginFields();
    const timers = [100, 500, 1200].map((delay) => window.setTimeout(clearLoginFields, delay));

    return () => {
      timers.forEach((timer) => window.clearTimeout(timer));
    };
  }, []);

  const validate = () => {
    const nextErrors: { email?: string; password?: string } = {};

    if (!email.trim()) {
      nextErrors.email = 'Email is required';
    } else if (!email.includes('@')) {
      nextErrors.email = 'Please enter a valid email address';
    }

    if (!password) {
      nextErrors.password = 'Password is required';
    } else if (password.length < 6) {
      nextErrors.password = 'Password must be at least 6 characters';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!validate()) {
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      await new Promise(resolve => setTimeout(resolve, 350));

      const account = LOGIN_ACCOUNTS.find(
        (candidate) =>
          normalizeEmail(candidate.email) === normalizeEmail(email) &&
          candidate.password === normalizePassword(password)
      );

      if (!account) {
        setErrors({ form: 'Invalid email or password. Please try again.' });
        toast.error('Login failed. Check the superadmin, carer, or family credentials.');
        return;
      }

      if (account.user.roles.includes('FAMILY')) {
        sessionStorage.setItem('familyMemberId', account.user.id);
        sessionStorage.setItem('clientId', 'client-1');
      } else {
        sessionStorage.removeItem('familyMemberId');
        sessionStorage.removeItem('clientId');
      }

      login(account.user, account.token);
      toast.success(account.welcome);
      navigate(getDashboardRoute(account.user.roles));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 lg:grid lg:grid-cols-[1.05fr_0.95fr]">
      <section className="relative hidden overflow-hidden bg-[#5e3474] lg:flex lg:flex-col lg:justify-between">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(255,255,255,0.18),transparent_32%),linear-gradient(135deg,rgba(94,52,116,0.96),rgba(37,51,74,0.98))]" />
        <div className="relative px-12 pt-12">
          <img
            src="/images/fuchsia-logo-full.png"
            alt="FUCHSIA HOME CARE CATALIN"
            className="h-36 w-auto object-contain"
          />
        </div>
        <div className="relative px-12 pb-14 text-white">
          <div className="mb-8 inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-2 text-sm font-semibold">
            <ShieldCheck className="h-4 w-4" />
            Superadmin access
          </div>
          <h1 className="max-w-xl text-5xl font-bold leading-tight tracking-normal">
            FUCHSIA HOME CARE CATALIN
          </h1>
          <p className="mt-5 max-w-xl text-lg leading-8 text-white/82">
            Yarmouth Business Park, Suffolk Rd, Great Yarmouth, NR31 0LN
          </p>
          <div className="mt-8 flex items-center gap-3 text-sm font-medium text-white/85">
            <Phone className="h-4 w-4" />
            <span>01493738488 | On Call 07443145811</span>
          </div>
        </div>
      </section>

      <main className="flex min-h-screen items-center justify-center px-5 py-10 sm:px-8">
        <div className="w-full max-w-md">
          <div className="rounded-lg border border-slate-200 bg-white shadow-xl">
            <div className="space-y-3 px-8 pb-4 pt-8 text-center">
              <div className="flex justify-center">
                <img
                  src="/images/fuchsia-logo-mark.png"
                  alt="FUCHSIA HOME CARE CATALIN"
                  className="h-20 w-20 rounded-lg object-contain"
                />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-slate-950">FUCHSIA HOME CARE CATALIN</h2>
                <p className="mt-1 text-sm text-slate-500">
                  Conecteaza-te in aplicatia de home care
                </p>
                <div className="mt-4 rounded-md bg-slate-50 px-4 py-3 text-left text-xs leading-5 text-slate-600">
                  <p className="font-semibold text-slate-800">
                    Yarmouth Business Park, Suffolk Rd, Great Yarmouth, NR31 0LN
                  </p>
                  <p>Tel: 01493738488 | On Call: 07443145811</p>
                  <p>Email: lowestoft@fuchsiahomecare.co.uk</p>
                </div>
              </div>
            </div>

            <div className="px-8 pb-8">
              <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
                <div className="space-y-2">
                  <label htmlFor="email" className="text-sm font-medium text-slate-800">
                    Email
                  </label>
                  <div className="relative">
                    <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <input
                      id="email"
                      name="fuchsia-login-email"
                      type="email"
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      placeholder=""
                      autoComplete="off"
                      autoCorrect="off"
                      autoCapitalize="none"
                      spellCheck={false}
                      disabled={isLoading}
                      className="h-11 w-full rounded-md border border-slate-300 bg-white pl-10 pr-3 text-sm text-slate-950 outline-none transition focus:border-[#6c3a80] focus:ring-2 focus:ring-[#6c3a80]/20 disabled:cursor-not-allowed disabled:bg-slate-50"
                    />
                  </div>
                  {errors.email && <p className="text-sm text-red-600">{errors.email}</p>}
                </div>

                <div className="space-y-2">
                  <label htmlFor="password" className="text-sm font-medium text-slate-800">
                    Password
                  </label>
                  <div className="relative">
                    <LockKeyhole className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <input
                      id="password"
                      name="fuchsia-login-password"
                      type="password"
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      placeholder="Enter your password"
                      autoComplete="new-password"
                      disabled={isLoading}
                      className="h-11 w-full rounded-md border border-slate-300 bg-white pl-10 pr-3 text-sm text-slate-950 outline-none transition focus:border-[#6c3a80] focus:ring-2 focus:ring-[#6c3a80]/20 disabled:cursor-not-allowed disabled:bg-slate-50"
                    />
                  </div>
                  {errors.password && <p className="text-sm text-red-600">{errors.password}</p>}
                </div>

                {errors.form && (
                  <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                    {errors.form}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isLoading}
                  className="inline-flex h-11 w-full items-center justify-center rounded-md bg-[#6c3a80] px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-[#5e3474] focus:outline-none focus:ring-2 focus:ring-[#6c3a80] focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Sign in
                </button>
              </form>

              <div className="mt-6 text-center text-sm text-slate-500">
                Ai nevoie de acces?{' '}
                <a href="tel:01493738488" className="font-medium text-[#6c3a80] hover:underline">
                  Contacteaza administratorul
                </a>
              </div>
            </div>
          </div>

        </div>
      </main>
    </div>
  );
};

